// A new purchase request from scratch (M6): one supplier, how many packs of each of its
// items, when it is needed. It goes to the approvals inbox; approving makes the order
// (D-060). From the restock suggestion, requests are made on the restock page instead.
// Each row shows what is on hand and on order, the par level and the suggestion, and when it
// was last ordered; a running estimate says what it leaves out (UI6A-13).

import type { RestockItem } from '@enp/domain/api';

import type { SupplierDetail } from '@enp/domain/api';
import { cents, mulQty } from '@enp/domain/money';
import { isPositive } from '@enp/domain/quantity';
import {
  Button,
  DateField,
  EmptyState,
  FormField,
  Input,
  Money,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
  toast,
} from '@enp/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Lightbulb, PackagePlus, Send, TriangleAlert, Truck } from 'lucide-react';
import { Fragment, useMemo, useRef, useState, type FormEvent } from 'react';
import { newId } from '@enp/domain/ids';
import { meQueryOptions } from '../../../../auth/session.js';
import {
  requestedQty,
  shelfLifeWarning,
  suggestionOutcome,
} from '../../../../purchasing/figures.js';
import {
  BaseQty,
  NoPermission,
  PurchasingBreadcrumb,
  useBranches,
  useCan,
} from '../../../../purchasing/parts.js';
import {
  allSuppliersQuery,
  createRequest,
  invalidatePurchasing,
  newRequestSearchSchema,
  restockQuery,
  supplierQuery,
} from '../../../../purchasing/queries.js';
import { packsError, packsText, packText, parsePacks } from '../../../../purchasing/words.js';
import { AccountDate } from '../../../../shell/account-date.js';
import { useDocumentTitle } from '../../../../shell/page.js';
import { ErrorPanel } from '../../../../shell/route-states.js';
import { Notice } from '../../../../stations/page-parts.js';
import { accountToday } from '../../../../stock/labels.js';
import { actionProblem } from '../../../../stock-control/parts.js';

export const Route = createFileRoute('/_app/purchasing/requests/new')({
  validateSearch: newRequestSearchSchema,
  component: NewRequestPage,
});

function ItemsTable({
  supplier,
  packs,
  setPacks,
  errors,
  stock,
  stockLoading,
  stockFailed,
}: {
  supplier: SupplierDetail;
  packs: Record<string, string>;
  setPacks: (id: string, v: string) => void;
  errors: Record<string, string>;
  /** The restock suggestion's figures per product (on hand, on order, par, suggestion). */
  stock: Map<string, RestockItem>;
  stockLoading: boolean;
  /** The stock figures failed to load. */
  stockFailed: boolean;
}) {
  const live = supplier.items.filter((i) => i.archived_at === null);
  if (live.length === 0) {
    return (
      <EmptyState
        compact
        icon={PackagePlus}
        title={`Nothing to order from ${supplier.name} yet`}
        description="Add what you buy from this supplier (the product, its pack and its price) on the supplier’s page first."
        action={
          <Button variant="secondary" asChild>
            <Link to="/purchasing/suppliers/$supplierId" params={{ supplierId: supplier.id }}>
              Open {supplier.name}
            </Link>
          </Button>
        }
      />
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table aria-label="What to order" className="w-full border-collapse text-body">
        <thead>
          <tr className="bg-surface-sunken text-left text-xs font-semibold uppercase tracking-wide text-muted">
            <th scope="col" className="px-3 py-2">
              Product
            </th>
            <th scope="col" className="px-3 py-2">
              Pack
            </th>
            <th scope="col" className="px-3 py-2">
              Stock now
            </th>
            <th scope="col" className="hidden px-3 py-2 lg:table-cell">
              Last ordered
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Price per pack
            </th>
            <th scope="col" className="w-36 px-3 py-2">
              Packs
            </th>
            <th scope="col" className="px-3 py-2 text-right">
              Estimate
            </th>
          </tr>
        </thead>
        <tbody>
          {live.map((i) => {
            const typed = packs[i.id] ?? '';
            const n = parsePacks(typed);
            const price = i.current_price?.net_price_cents ?? null;
            const s = stock.get(i.product.id);
            const unit = i.product.base_uom;
            const suggestion = s ? suggestionOutcome(s) : null;
            // More than it sells before its best-before (UI6A-108).
            const tooMuch = n ? shelfLifeWarning(n, i.base_qty_per_pack, s) : null;
            const warned = !errors[i.id] && tooMuch !== null;
            return (
              <Fragment key={i.id}>
                <tr className="border-t border-border align-top" data-item={i.id}>
                  <td className="px-3 py-2">
                    <span className="flex flex-col leading-tight">
                      <span className="font-medium text-fg">{i.product.name}</span>
                      <span className="text-xs text-muted">
                        {i.supplier_code ? (
                          <>
                            Their code <span className="whitespace-nowrap">{i.supplier_code}</span>
                          </>
                        ) : (
                          'No supplier code'
                        )}
                      </span>
                      {price === null && (
                        <span className="mt-1 flex items-center gap-1 text-xs font-medium text-state-warning">
                          <TriangleAlert aria-hidden className="size-3.5" />
                          No price: left out of the estimate
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-fg">{packText(i, unit)}</td>
                  <td className="px-3 py-2 text-sm" data-testid="stock-now">
                    {s ? (
                      <span className="flex flex-col leading-tight">
                        <span className="text-fg">
                          <BaseQty value={s.explanation.inputs.on_hand_qty} unit={unit} /> on hand
                        </span>
                        <span className="text-xs text-muted">
                          <BaseQty value={s.explanation.inputs.on_order_qty} unit={unit} /> on order
                          {isPositive(requestedQty(s)) && (
                            <span className="font-medium text-state-warning">
                              {' '}
                              · <BaseQty value={requestedQty(s)} unit={unit} /> already asked
                            </span>
                          )}
                          {s.explanation.inputs.par_qty !== null && (
                            <>
                              {' '}
                              · par <BaseQty value={s.explanation.inputs.par_qty} unit={unit} />
                            </>
                          )}
                        </span>
                        {suggestion && (
                          <span className="text-xs text-muted">Suggested: {suggestion.text}</span>
                        )}
                      </span>
                    ) : stockLoading ? (
                      <Skeleton className="h-4 w-24" />
                    ) : (
                      <span className="text-muted">
                        {stockFailed ? 'Didn’t load' : 'Not known'}
                      </span>
                    )}
                  </td>
                  <td className="hidden px-3 py-2 text-sm lg:table-cell">
                    {i.last_ordered ? (
                      <span className="flex flex-col leading-tight">
                        <span className="text-fg">
                          {packsText(i.last_ordered.qty_packs, i.pack_uom)}
                        </span>
                        <span className="text-xs text-muted">
                          <AccountDate value={i.last_ordered.ordered_at} /> ·{' '}
                          <span className="whitespace-nowrap font-mono">
                            {i.last_ordered.po_number}
                          </span>
                        </span>
                      </span>
                    ) : (
                      <span className="text-muted">Never</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {price === null ? (
                      <span className="text-sm text-muted">No price</span>
                    ) : (
                      <Money cents={price} className="text-fg" />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="flex items-center gap-2">
                      <Input
                        inputMode="decimal"
                        aria-label={`Packs of ${i.product.name}`}
                        aria-invalid={errors[i.id] ? true : undefined}
                        aria-describedby={warned ? `shelf-life-${i.id}` : undefined}
                        value={typed}
                        placeholder="0"
                        onChange={(e) => setPacks(i.id, e.target.value)}
                        className="w-24 text-right tabular-nums"
                      />
                      <span aria-hidden className="whitespace-nowrap text-sm text-muted">
                        {packsText('2', i.pack_uom).replace(/^2 /, '')}
                      </span>
                    </span>
                    {errors[i.id] && (
                      <p role="alert" className="mt-1 text-xs font-medium text-state-danger">
                        {errors[i.id]}
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {n && price !== null ? (
                      <Money cents={mulQty(cents(price), n)} className="text-fg" />
                    ) : (
                      <span className="text-sm text-muted">—</span>
                    )}
                  </td>
                </tr>
                {/* Under the line, full width, as restock and the inbox say theirs (UI8C-9). */}
                {warned && (
                  <tr data-warning-for={i.id}>
                    <td colSpan={7} className="px-3 pb-2 pt-0">
                      <p
                        id={`shelf-life-${i.id}`}
                        className="flex items-start gap-2 text-sm font-medium text-fg"
                        data-testid="shelf-life-warning"
                      >
                        <TriangleAlert
                          aria-hidden
                          className="mt-0.5 size-4 shrink-0 text-state-warning"
                        />
                        {tooMuch}
                      </p>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NewRequestPage() {
  useDocumentTitle('New purchase request');
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { data: me } = useSuspenseQuery(meQueryOptions());
  const today = accountToday(me.account);
  const canRequest = useCan('purchasing.request');
  const suppliers = useQuery(allSuppliersQuery());
  const supplier = useQuery({
    ...supplierQuery(search.supplier ?? ''),
    enabled: Boolean(search.supplier),
  });
  const { branches } = useBranches();
  const [branchId, setBranchId] = useState<string>();
  // An empty choice is no choice: the default branch (B-081).
  const branch = branchId || branches[0]?.id;
  const [neededBy, setNeededBy] = useState<string>();
  const [note, setNote] = useState('');
  const [packs, setPacksState] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string>();

  // Checked as typed, so the button and the field never disagree.
  const setPacks = (id: string, v: string) => {
    setPacksState((p) => ({ ...p, [id]: v }));
    setErrors((e) => ({ ...e, [id]: packsError(v) ?? '' }));
    setFormError(undefined);
  };
  const stock = useQuery({
    ...restockQuery({ supplier: search.supplier, branch, all: true }),
    // Without a branch yet, the account's default branch (the server's default): no wait on it.
    enabled: Boolean(search.supplier),
  });
  const stockBy = useMemo(
    () => new Map((stock.data?.items ?? []).map((s) => [s.product.id, s])),
    [stock.data],
  );
  const live = supplier.data?.items.filter((i) => i.archived_at === null) ?? [];
  const chosen = live
    .map((i) => ({ i, n: parsePacks(packs[i.id] ?? '') }))
    .filter((x): x is { i: (typeof live)[number]; n: string } => x.n !== null);
  const estimate = chosen.reduce(
    (sum, { i, n }) =>
      i.current_price ? sum + mulQty(cents(i.current_price.net_price_cents), n) : sum,
    0,
  );
  const unpriced = chosen.filter(({ i }) => i.current_price === null).length;
  const hasErrors = Object.values(errors).some(Boolean);

  // One key per form: a retried send answers the first request (D-063).
  const attempt = useRef(newId());
  const create = useMutation({
    mutationFn: (body: Parameters<typeof createRequest>[0]) => createRequest(body, attempt.current),
    onSuccess: async (r) => {
      await invalidatePurchasing(queryClient);
      toast.success(`${r.number} sent for approval`, {
        description: 'It waits in the approvals inbox. Approving makes the purchase order.',
      });
      void navigate({ to: '/purchasing/requests/$requestId', params: { requestId: r.id } });
    },
  });
  const failed = create.isError ? actionProblem(create.error, 'The request wasn’t sent') : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!supplier.data) return;
    const nextErrors: Record<string, string> = {};
    const lines: { supplier_item_id: string; qty_packs: string }[] = [];
    for (const item of supplier.data.items.filter((i) => i.archived_at === null)) {
      const typed = (packs[item.id] ?? '').trim();
      if (typed === '' || typed === '0') continue;
      const n = parsePacks(typed);
      if (!n) nextErrors[item.id] = 'A number of packs above 0, like 2 or 3.';
      else lines.push({ supplier_item_id: item.id, qty_packs: n });
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    if (lines.length === 0) {
      setFormError('Type how many packs of at least one product.');
      return;
    }
    if (!branch) {
      setFormError('Choose the branch it is for.');
      return;
    }
    create.mutate({
      branch_id: branch,
      supplier_id: supplier.data.id,
      origin: 'admin',
      lines,
      ...(neededBy ? { needed_by: neededBy } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  const liveSuppliers = (suppliers.data?.items ?? []).filter((s) => s.archived_at === null);

  return (
    <>
      <PageHeader
        eyebrow={
          <PurchasingBreadcrumb
            trail={[{ to: '/purchasing/requests', label: 'Requests' }]}
            page="New request"
          />
        }
        title="New purchase request"
        description="Ask for stock from one supplier. A manager approves it product by product in the approvals inbox; approving makes the order."
        actions={
          <Button variant="secondary" asChild>
            <Link to="/purchasing/restock">
              <Lightbulb aria-hidden />
              Use the restock suggestion instead
            </Link>
          </Button>
        }
      />
      {canRequest === false ? (
        <NoPermission>
          Asking for stock needs the “request purchases” permission. Ask the owner.
        </NoPermission>
      ) : suppliers.isError ? (
        <ErrorPanel
          error={suppliers.error}
          titleAs="h2"
          what="The suppliers"
          onRetry={() => void suppliers.refetch()}
        />
      ) : suppliers.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : liveSuppliers.length === 0 ? (
        <EmptyState
          icon={Truck}
          title="No suppliers yet"
          description="Add a supplier and what you buy from them before asking for stock."
          action={
            <Button asChild>
              <Link to="/purchasing/suppliers">Open suppliers</Link>
            </Button>
          }
        />
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-5" noValidate>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FormField label="Supplier" required>
              {(control) => (
                <Select
                  value={search.supplier ?? ''}
                  onValueChange={(v) => {
                    setPacksState({});
                    setErrors({});
                    void navigate({ search: { supplier: v }, replace: true });
                  }}
                >
                  <SelectTrigger {...control}>
                    <SelectValue placeholder="Choose a supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {liveSuppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
            <FormField label="For branch" required>
              {(control) => (
                <Select value={branch ?? ''} onValueChange={setBranchId}>
                  <SelectTrigger {...control}>
                    <SelectValue placeholder="Choose a branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
            <FormField label="Needed by" description="Becomes the order’s expected date.">
              {(control) => (
                <DateField
                  {...control}
                  value={neededBy}
                  onChange={setNeededBy}
                  today={today}
                  min={today}
                  placeholder="Any day"
                />
              )}
            </FormField>
            <FormField label="Note (optional)">
              <Textarea
                rows={1}
                value={note}
                maxLength={1000}
                onChange={(e) => setNote(e.target.value)}
              />
            </FormField>
          </div>
          {!search.supplier ? (
            <p className="text-body text-muted">Choose a supplier to see what you buy from them.</p>
          ) : supplier.isError ? (
            <ErrorPanel
              error={supplier.error}
              titleAs="h2"
              what="The supplier’s items"
              onRetry={() => void supplier.refetch()}
            />
          ) : !supplier.data ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <ItemsTable
              supplier={supplier.data}
              packs={packs}
              setPacks={setPacks}
              errors={errors}
              stock={stockBy}
              stockLoading={stock.isPending}
              stockFailed={stock.isError}
            />
          )}
          {formError && (
            <p role="alert" className="text-body font-medium text-state-danger">
              {formError}
            </p>
          )}
          {failed && (
            <Notice tone="danger" alert title={failed.title} technical={failed.technical}>
              {failed.explain}
            </Notice>
          )}
          <div className="flex flex-wrap items-center justify-end gap-3 rounded-lg border border-border bg-surface-raised px-4 py-3 shadow-xs">
            <p className="mr-auto text-body text-fg" aria-live="polite" data-testid="request-total">
              {chosen.length === 0 ? (
                'Nothing chosen yet.'
              ) : (
                <>
                  {chosen.length} {chosen.length === 1 ? 'product' : 'products'} ·{' '}
                  {unpriced === chosen.length ? (
                    // Nothing priced: no estimate, never "R 0.00" (UI6A-108).
                    <span className="font-semibold">No estimate yet</span>
                  ) : (
                    <>
                      Estimate <Money cents={estimate} className="font-semibold" /> ex VAT
                    </>
                  )}
                  {unpriced > 0 && (
                    <span className="font-medium text-state-warning">
                      {' '}
                      + {unpriced} unpriced (left out)
                    </span>
                  )}
                </>
              )}
            </p>
            <Button type="button" variant="secondary" asChild>
              <Link to="/purchasing/requests">Cancel</Link>
            </Button>
            <Button
              type="submit"
              icon={<Send aria-hidden />}
              loading={create.isPending}
              disabled={!supplier.data || hasErrors}
            >
              Send for approval
            </Button>
          </div>
        </form>
      )}
    </>
  );
}
